"use client";

import { PlanTier } from "@cabal/shared/domain/billing/enums.js";
import type { PricingPlanAccessInput } from "@/components/ui/Cpricingmodal/pricingModalTypes";
import { useStartCheckout } from "@/contexts/subscription";
import {
  MobileModalDebugBranch,
  MobileModalDebugEvent,
  type PersistentDebugLogger,
  captureMobileModalEnvironment,
} from "@/hooks/telemetry";
import type { OpenAuthModalOptions } from "@/contexts/layout";
import {
  requestNewTeamModal,
  type RequestNewTeamModalInput,
} from "@/contexts/team-modal";
import { PostLoginIntentKind } from "@/domain/post-login-intent/enums";
import { requestAuthForPostLoginIntent } from "@/lib/postLoginIntent";
import type { HomePageInteractionState } from "./domain/home-page/types";

interface StartPlanCheckoutFlowInput {
  paddlePriceId: string;
  planKey?: PlanTier;
}

interface RunStartPlanCheckoutFlowInput {
  checkoutInput: StartPlanCheckoutFlowInput;
  hasActiveSubscription: boolean;
  openUserMenu: () => void;
  setPricingAccessLoadingPlanKey: (planKey: PlanTier | null) => void;
  closeAuthModal: () => void;
  setShowPricingModal: (value: boolean) => void;
  startCheckout: ReturnType<typeof useStartCheckout>;
}

interface RunPricingPlanAccessInput {
  accessInput: PricingPlanAccessInput;
  isAuthenticated: boolean;
  pricingAccessLoadingPlanKey: PlanTier | null;
  openAuthModal: (options?: OpenAuthModalOptions) => void;
  setShowPricingModal: (value: boolean) => void;
  startPlanCheckoutFlow: (input: StartPlanCheckoutFlowInput) => void;
}

interface RunBuildYourOwnTeamInput {
  interactionState: HomePageInteractionState;
  modalDebugLogger: PersistentDebugLogger;
  openAuthModal: (options?: OpenAuthModalOptions) => void;
  /** The shared build gate's authenticated branch (`openNew` + its origin). */
  requestNewTeam: Omit<
    RequestNewTeamModalInput,
    "isAuthenticated" | "openAuthModal"
  >;
}

interface RunOpenDeployForTeamInput {
  interactionState: HomePageInteractionState;
  modalDebugLogger: PersistentDebugLogger;
  openAuthModal: (options?: OpenAuthModalOptions) => void;
  /** Open the canonical team modal on this team with deploy invoked. */
  openDeployModal: () => void;
  teamId: string;
}

function getPreflightBranch(isAuthenticated: boolean): MobileModalDebugBranch {
  if (isAuthenticated) {
    return MobileModalDebugBranch.OpenPreflight;
  }

  return MobileModalDebugBranch.OpenAuth;
}

function getNewTeamBranch(isAuthenticated: boolean): MobileModalDebugBranch {
  if (isAuthenticated) {
    return MobileModalDebugBranch.OpenNewTeam;
  }

  return MobileModalDebugBranch.OpenAuth;
}

export function runStartPlanCheckoutFlow(
  input: RunStartPlanCheckoutFlowInput,
): void {
  if (input.hasActiveSubscription) {
    input.setShowPricingModal(false);
    input.closeAuthModal();
    input.openUserMenu();
    return;
  }

  if (input.checkoutInput.planKey) {
    input.setPricingAccessLoadingPlanKey(input.checkoutInput.planKey);
  }

  input.startCheckout.invoke(
    {
      paddlePriceId: input.checkoutInput.paddlePriceId,
    },
    {
      onSuccess: (checkoutResult) => {
        window.location.href = checkoutResult.checkoutUrl;
      },
      onSettled: () => {
        if (input.checkoutInput.planKey) {
          input.setPricingAccessLoadingPlanKey(null);
        }
      },
    },
  );
}

/**
 * A plan the user picked while signed out parks its checkout on the one
 * post-login carrier, so the gate goes up only once that record is down — see
 * `lib/postLoginIntent`. The signed-in branch reaches no `await` and still runs
 * inside the click.
 */
export async function runPricingPlanAccess(
  input: RunPricingPlanAccessInput,
): Promise<void> {
  if (
    input.pricingAccessLoadingPlanKey != null ||
    !input.accessInput.paddlePriceId
  ) {
    return;
  }

  if (!input.isAuthenticated) {
    input.setShowPricingModal(false);
    await requestAuthForPostLoginIntent({
      intent: {
        kind: PostLoginIntentKind.PlanCheckout,
        paddlePriceId: input.accessInput.paddlePriceId,
      },
      openAuthModal: input.openAuthModal,
    });
    return;
  }

  input.setShowPricingModal(false);
  input.startPlanCheckoutFlow({
    paddlePriceId: input.accessInput.paddlePriceId,
    planKey: input.accessInput.planKey,
  });
}

/**
 * "Build Your Own" — the canonical team modal on an EMPTY subject with edit
 * invoked. No record exists until its Save is pressed, so the logged-out
 * branch parks the intent alone and resumes into the same empty draft after
 * sign-in. The gate itself is the shared `requestNewTeamModal`, identical at
 * every Build entry; this landing wrapper only adds its telemetry, which is
 * logged before the gate is asked for and so keeps its place in the click.
 */
export async function runBuildYourOwnTeam(
  input: RunBuildYourOwnTeamInput,
): Promise<void> {
  input.modalDebugLogger.logBranchChoice({
    name: MobileModalDebugEvent.TeamsGalleryBuildYourOwn,
    selectedBranch: getNewTeamBranch(input.interactionState.isAuthenticated),
    payload: {
      showTeamsModal: input.interactionState.showTeamsModal,
      showTeamDetailsModal: input.interactionState.showTeamDetailsModal,
      teamDetailsTeamId: input.interactionState.teamDetailsTeamId,
      environment: captureMobileModalEnvironment(),
    },
  });
  await requestNewTeamModal({
    ...input.requestNewTeam,
    isAuthenticated: input.interactionState.isAuthenticated,
    openAuthModal: input.openAuthModal,
  });
}

/**
 * A deploy intent on an EXISTING team — hero Deploy, `?viewTeam&mode=deploy`
 * restores, the post-login deploy intent. Gate-before-open: logged out parks
 * the intent and raises the auth gate once that park is down; logged in opens
 * the canonical team modal with deploy invoked (back lands on that team's
 * regular state) without reaching an `await`.
 */
export async function runOpenDeployForTeam(
  input: RunOpenDeployForTeamInput,
): Promise<void> {
  input.modalDebugLogger.logBranchChoice({
    name: MobileModalDebugEvent.OpenPreflightForTeam,
    selectedBranch: getPreflightBranch(input.interactionState.isAuthenticated),
    payload: {
      teamId: input.teamId,
      showTeamsModal: input.interactionState.showTeamsModal,
      showTeamDetailsModal: input.interactionState.showTeamDetailsModal,
      currentTeamDetailsTeamId: input.interactionState.teamDetailsTeamId,
      environment: captureMobileModalEnvironment(),
    },
  });
  if (!input.interactionState.isAuthenticated) {
    await requestAuthForPostLoginIntent({
      intent: {
        kind: PostLoginIntentKind.TeamModalDeploy,
        teamId: input.teamId,
      },
      openAuthModal: input.openAuthModal,
    });
    return;
  }

  input.openDeployModal();
}
